import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  AlertCircle,
  TrendingUp,
  MapPin,
  Building,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CasesPage = () => {
  const navigate = useNavigate();
  const [caseload, setCaseload] = useState({ cases: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [totalCases, setTotalCases] = useState(0);
  const limit = 20;

  // For admin/supervisor view
  const [allCases, setAllCases] = useState([]);
  const [viewMode, setViewMode] = useState('caseload'); // 'caseload' or 'all'

  useEffect(() => {
    fetchCaseload();
  }, [stateFilter, programFilter]);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllCases();
    }
  }, [page, statusFilter, priorityFilter, programFilter, stateFilter, viewMode]);

  const fetchCaseload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.append('state', stateFilter);
      if (programFilter) params.append('program_type', programFilter);

      const response = await axios.get(`${API}/dashboard/my-caseload?${params}`);
      setCaseload(response.data);
    } catch (error) {
      console.error('Failed to fetch caseload:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * limit).toString(),
        limit: limit.toString()
      });
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (programFilter) params.append('program_type', programFilter);
      if (stateFilter) params.append('state', stateFilter);

      const response = await axios.get(`${API}/cases?${params}`);
      setAllCases(response.data.items);
      setTotalCases(response.data.total);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const displayedCases = viewMode === 'caseload' ? caseload.cases : allCases;
  const filteredCases = displayedCases.filter(c => {
    if (viewMode === 'caseload') {
      if (priorityFilter && c.priority !== priorityFilter) return false;
      if (statusFilter && c.status !== statusFilter) return false;
    }
    return true;
  });

  const totalPages = viewMode === 'all' ? Math.ceil(totalCases / limit) : 1;

  return (
    <div className="space-y-6" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {viewMode === 'caseload' ? 'My Caseload' : 'All Cases'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {viewMode === 'caseload' 
              ? 'Your assigned cases organized by program and state'
              : 'Browse all cases in the system'}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="caseload">My Caseload</SelectItem>
              <SelectItem value="all">All Cases</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Caseload Summary (only in caseload mode) */}
      {viewMode === 'caseload' && caseload.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Active</p>
                  <p className="text-2xl font-bold">{caseload.summary.total || 0}</p>
                </div>
                <FolderOpen className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-shadow border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">High Priority</p>
                  <p className="text-2xl font-bold text-red-600">{caseload.summary.high_priority || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-shadow border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Medium Priority</p>
                  <p className="text-2xl font-bold text-yellow-600">{caseload.summary.medium_priority || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-shadow border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Low Priority</p>
                  <p className="text-2xl font-bold text-green-600">{caseload.summary.low_priority || 0}</p>
                </div>
                <Users className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Filters (States/Programs from caseload) */}
      {viewMode === 'caseload' && caseload.summary && (
        <div className="flex flex-wrap gap-2">
          {caseload.summary.states?.map((state) => (
            <Badge 
              key={state}
              variant={stateFilter === state ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setStateFilter(stateFilter === state ? '' : state)}
            >
              <MapPin className="w-3 h-3 mr-1" />
              {state}
            </Badge>
          ))}
          {caseload.summary.programs?.map((program) => (
            <Badge 
              key={program}
              variant={programFilter === program ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setProgramFilter(programFilter === program ? '' : program)}
            >
              <Building className="w-3 h-3 mr-1" />
              {program.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter || "all"} onValueChange={(val) => setPriorityFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="priority-filter">
                <SelectValue placeholder="All Priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            {viewMode === 'all' && (
              <>
                <Select value={stateFilter || "all"} onValueChange={(val) => setStateFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-full sm:w-32" data-testid="state-filter">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    <SelectItem value="OH">Ohio</SelectItem>
                    <SelectItem value="KY">Kentucky</SelectItem>
                    <SelectItem value="IN">Indiana</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}

            <Button 
              variant="secondary" 
              onClick={() => { 
                setStatusFilter(''); 
                setPriorityFilter(''); 
                setStateFilter('');
                setProgramFilter('');
              }}
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="card-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Cases</CardTitle>
            <span className="text-sm text-muted-foreground">
              {filteredCases.length} cases
              {viewMode === 'all' && ` of ${totalCases}`}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48" data-testid="loading-spinner">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredCases.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="cases-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Case ID</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Participant</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Program</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">State</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCases.map((caseItem) => (
                      <tr 
                        key={caseItem.id} 
                        className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/cases/${caseItem.id}`)}
                        data-testid={`case-row-${caseItem.id}`}
                      >
                        <td className="py-3 px-4 text-sm font-mono text-primary">{caseItem.id}</td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{caseItem.participant_name}</div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {caseItem.service_type_name || caseItem.service_type?.replace(/_/g, ' ') || caseItem.case_type?.replace(/_/g, ' ')}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {caseItem.program_name || caseItem.program_type?.replace(/_/g, ' ')}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium">{caseItem.state || '-'}</td>
                        <td className="py-3 px-4">
                          <Badge className={getPriorityColor(caseItem.priority)}>
                            {caseItem.priority}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(caseItem.status)}>
                            {caseItem.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/cases/${caseItem.id}`);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination (only for all cases mode) */}
              {viewMode === 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalCases)} of {totalCases}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12" data-testid="no-results">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <h3 className="text-lg font-medium">No cases found</h3>
              <p className="text-muted-foreground mt-1">
                {viewMode === 'caseload' 
                  ? 'You have no assigned cases matching the current filters'
                  : 'Try adjusting your filters'}
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                To create a new case, go to a <Link to="/participants" className="text-primary hover:underline">participant&apos;s profile</Link> and open a case under their program enrollment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CasesPage;
